import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { formatDateParam, parseDateParam } from '@/lib/utils/date-param';
import type { DateRange } from '@/components/shared/date-range-picker';
import type {
  ListAdminTransactionsParams,
  GatewayMode,
} from '@/types/admin-transaction';

/**
 * Issue #300: Filter-Persistenz der TX-Liste ueber die URL (`useSearchParams`)
 * statt lokalem `useState`. Damit ueberleben Filter den Wechsel in die
 * Detailansicht, sind per Reload/Link rekonstruierbar und trivial resetbar.
 *
 * Spec 409: die Service/Status/Operator-Filter sind Mehrfach+Ausschluss und liegen
 * als `+/-`-Kommalisten unter `service`/`status`/`operator` in der URL; `txId` ist
 * eine Teilstringsuche. merchantId/flowType/gatewayMode bleiben einwertig. Das BFF
 * liest exakt diese Param-Namen. Die Achsen-Kodierung macht die Filterleiste
 * (lib/utils/filter-axis); dieser Hook reicht die Strings nur durch.
 *
 * Spec 380: zusaetzlich „zuletzt genutzt" via localStorage-Snapshot. Die URL bleibt
 * Source-of-Truth; aus dem Snapshot wird nur vorbelegt, wenn die URL keine Filter
 * traegt (Deep-Links/Share gewinnen). Der Snapshot spiegelt jede URL-Aenderung,
 * leeren Zustand eingeschlossen. Der Cursor wird nie persistiert (Restore → Seite 1).
 *
 * Migration: der Snapshot-Key wurde v1→v2 gebumpt (das alte Schema trug einwertige
 * `serviceId`/`status`). Alte v1-Snapshots werden ignoriert (kein Fehl-Parse). Ein
 * Legacy-Deep-Link mit `?serviceId=` wird weiterhin gelesen (als einzelner Include
 * von `service`) und beim naechsten Setzen verworfen.
 *
 * Spec 475: Suchbegriffe (`txId` + die zwei v2.17-Server-Suchen
 * `internetqTransactionId`/`customerReference`) leben in ZWEI getrennten Key-Mengen:
 * sie zaehlen als aktive URL-Filter (Seed-Unterdrueckung — ein Deep-Link, der NUR
 * einen Suchbegriff traegt, darf nicht vom Snapshot ueberlagert werden; Reset-
 * Sichtbarkeit), werden aber NIE in den Snapshot persistiert — ein re-seedeter
 * Suchbegriff wuerde beim naechsten Besuch unaufgefordert eine teure Facade-Suche
 * feuern (customerReference = Tabellen-Scan). `txId` wechselt damit bewusst in die
 * Nicht-Persistenz (war bis Spec 409 geseedet); Alt-Snapshots mit `txId`-Wert
 * werden beim Seeding schlicht ignoriert, der Snapshot-Key bleibt v2.
 */

export const DEFAULT_TX_LIMIT = 25;

const TX_FILTERS_KEY = 'transactions.filters.v2';

/** Spec 475/#482: Suchbegriffe — aktive URL-Filter, aber NIE snapshot-persistiert. */
export const TX_SEARCH_PARAM_KEYS = [
  'txId',
  'internetqTransactionId',
  'customerReference',
  'customerReferenceExact',
] as const;

/** Kanonische Keys, die in den localStorage-Snapshot gespiegelt/geseedet werden. */
export const TX_PERSISTED_FILTER_KEYS = [
  'merchantId',
  'service',
  'status',
  'operator',
  'flowType',
  'gatewayMode',
  'limit',
  'amountMin',
  'amountMax',
  'dateFrom',
  'dateTo',
] as const;

// Alles, was einen aktiven Filter markiert (Cursor liegt nicht in der URL).
export const TX_ACTIVE_FILTER_KEYS = [
  ...TX_PERSISTED_FILTER_KEYS,
  ...TX_SEARCH_PARAM_KEYS,
] as const;

/** Snapshot in URL-Form (alle Werte Strings, wie sie in der Query stehen). */
type TxFilterSnapshot = { [K in (typeof TX_PERSISTED_FILTER_KEYS)[number]]?: string };

function isTxFilterSnapshot(v: unknown): v is TxFilterSnapshot {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isNumericString(v: unknown): v is string {
  return typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v));
}

function isNonDefaultLimit(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 && n !== DEFAULT_TX_LIMIT;
}

/**
 * Aktuellen URL-Filterzustand als Snapshot abbilden. Bewusst NUR die
 * Persistenz-Keys — Suchbegriffe (Spec 475) werden nie gespiegelt.
 */
function txSnapshotFromParams(params: URLSearchParams): TxFilterSnapshot {
  const snap: TxFilterSnapshot = {};
  for (const key of TX_PERSISTED_FILTER_KEYS) {
    const v = params.get(key);
    if (v !== null && v !== '') snap[key] = v;
  }
  return snap;
}

/**
 * Snapshot → URLSearchParams mit Feld-Sanitisierung (§4.4): ein ungültiges Feld faellt
 * einzeln weg, gueltige seeden weiter. Die Mehrfach-Achsen (service/status/operator)
 * sind freie Strings — das BFF validiert/verwirft unbekannte Enum-Werte; hier reicht
 * die Nicht-Leer-Pruefung. Enum-/Datums-/Zahlwerte werden wie bisher streng geprueft.
 * Suchbegriffe (Spec 475) seeden nie — siehe TX_SEARCH_PARAM_KEYS.
 */
function buildTxSeedParams(s: TxFilterSnapshot): URLSearchParams {
  const p = new URLSearchParams();
  if (typeof s.merchantId === 'string' && s.merchantId) p.set('merchantId', s.merchantId);
  if (typeof s.service === 'string' && s.service) p.set('service', s.service);
  if (typeof s.status === 'string' && s.status) p.set('status', s.status);
  if (typeof s.operator === 'string' && s.operator) p.set('operator', s.operator);
  // Spec 475: KEIN Suchbegriff-Seeding — `txId` in Alt-Snapshots wird ignoriert.
  if (s.flowType === 'WEB_TAN' || s.flowType === 'WAP_CLICK') p.set('flowType', s.flowType);
  if (s.gatewayMode === 'live' || s.gatewayMode === 'mock') p.set('gatewayMode', s.gatewayMode);
  if (isNumericString(s.amountMin)) p.set('amountMin', s.amountMin);
  if (isNumericString(s.amountMax)) p.set('amountMax', s.amountMax);
  if (typeof s.dateFrom === 'string' && parseDateParam(s.dateFrom)) p.set('dateFrom', s.dateFrom);
  if (typeof s.dateTo === 'string' && parseDateParam(s.dateTo)) p.set('dateTo', s.dateTo);
  if (isNonDefaultLimit(s.limit)) p.set('limit', s.limit);
  return p;
}

function setOrDelete(params: URLSearchParams, key: string, value: string | undefined | null): void {
  if (value == null || value === '') params.delete(key);
  else params.set(key, value);
}

export interface UseTransactionFilterParams {
  /** Server-Filter inkl. limit (ohne cursor — der bleibt lokal in der Seite). */
  filters: ListAdminTransactionsParams;
  amountMin: string;
  amountMax: string;
  dateFrom?: Date;
  dateTo?: Date;
  /** True, sobald irgendein Filter (inkl. Nicht-Default-limit) gesetzt ist. */
  hasActiveFilters: boolean;
  /** Server-Filter setzen (cursor-Reset macht die Seite ueber den cursorStack). */
  setFilters: (next: ListAdminTransactionsParams) => void;
  setAmountMin: (value: string) => void;
  setAmountMax: (value: string) => void;
  setDateRange: (range: DateRange) => void;
  /** Alle Filter loeschen → nackte URL `/transactions`, limit zurueck auf Default. */
  reset: () => void;
}

export function useTransactionFilterParams(): UseTransactionFilterParams {
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshot, setSnapshot] = useLocalStorage<TxFilterSnapshot>(
    TX_FILTERS_KEY,
    {},
    isTxFilterSnapshot,
  );

  const filters = useMemo<ListAdminTransactionsParams>(() => {
    const limitRaw = Number(searchParams.get('limit'));
    const limit = Number.isFinite(limitRaw) && limitRaw > 0 ? limitRaw : DEFAULT_TX_LIMIT;
    // Legacy read-compat: `?serviceId=svc_a` (vor Spec 409) → ein einzelner Include von `service`.
    const legacyServiceId = searchParams.get('serviceId');
    const service =
      searchParams.get('service') ??
      (legacyServiceId ? `+${legacyServiceId}` : undefined);
    return {
      merchantId: searchParams.get('merchantId') ?? undefined,
      service,
      status: searchParams.get('status') ?? undefined,
      operator: searchParams.get('operator') ?? undefined,
      txId: searchParams.get('txId') ?? undefined,
      // Spec 475/#482: Server-Suchen (exakt / Teilstring), Param-Namen = Facade-Namen.
      internetqTransactionId: searchParams.get('internetqTransactionId') ?? undefined,
      customerReference: searchParams.get('customerReference') ?? undefined,
      customerReferenceExact: searchParams.get('customerReferenceExact') ?? undefined,
      flowType: (searchParams.get('flowType') as 'WEB_TAN' | 'WAP_CLICK' | null) ?? undefined,
      gatewayMode: (searchParams.get('gatewayMode') as GatewayMode | null) ?? undefined,
      limit,
    };
  }, [searchParams]);

  const amountMin = searchParams.get('amountMin') ?? '';
  const amountMax = searchParams.get('amountMax') ?? '';
  const dateFrom = useMemo(() => parseDateParam(searchParams.get('dateFrom')), [searchParams]);
  const dateTo = useMemo(() => parseDateParam(searchParams.get('dateTo')), [searchParams]);

  // Legacy `serviceId` zaehlt mit, damit ein Deep-Link nicht vom Snapshot ueberschrieben wird.
  // Spec 475: die ACTIVE-Menge schliesst die Suchbegriffe ein — ein Deep-Link mit NUR
  // einem Suchbegriff unterdrueckt das Seeding (sonst ueberlagert ihn der Snapshot).
  const hasActiveFilters =
    TX_ACTIVE_FILTER_KEYS.some((key) => searchParams.has(key)) || searchParams.has('serviceId');

  // Spec 380: zwei Refs schuetzen die zwei bekannten Effekt-Fallen (s. Header).
  const seededRef = useRef(false);
  const skipNextMirrorRef = useRef(false);

  // Seed-on-mount: leere URL + nicht-leerer (sanitisierter) Snapshot → URL vorbelegen.
  // Laeuft genau einmal (seededRef); URL mit Filtern gewinnt (Deep-Link/Share).
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (hasActiveFilters) return;
    const seeded = buildTxSeedParams(snapshot);
    if (Array.from(seeded.keys()).length > 0) {
      skipNextMirrorRef.current = true; // den vom Seed ausgeloesten Mirror ueberspringen
      setSearchParams(seeded, { replace: true });
    }
  }, [hasActiveFilters, snapshot, setSearchParams]);

  // Spiegeln: jede URL-Aenderung (Setter ODER extern: Back/Forward, Deep-Link) in den
  // Snapshot, leeren Zustand eingeschlossen → kein stale Reseed. setSnapshot aendert
  // searchParams nicht, daher keine Effekt-Schleife.
  useEffect(() => {
    if (!seededRef.current) return; // erst nach der Seed-Entscheidung spiegeln
    if (skipNextMirrorRef.current) {
      skipNextMirrorRef.current = false;
      return;
    }
    setSnapshot(txSnapshotFromParams(searchParams));
  }, [searchParams, setSnapshot]);

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setFilters = useCallback(
    (next: ListAdminTransactionsParams) => {
      update((params) => {
        setOrDelete(params, 'merchantId', next.merchantId);
        setOrDelete(params, 'service', next.service);
        setOrDelete(params, 'status', next.status);
        setOrDelete(params, 'operator', next.operator);
        setOrDelete(params, 'txId', next.txId);
        setOrDelete(params, 'internetqTransactionId', next.internetqTransactionId);
        setOrDelete(params, 'customerReference', next.customerReference);
        setOrDelete(params, 'customerReferenceExact', next.customerReferenceExact);
        setOrDelete(params, 'flowType', next.flowType);
        setOrDelete(params, 'gatewayMode', next.gatewayMode);
        // Legacy-Param aufraeumen, sobald irgendein Filter gesetzt wird.
        params.delete('serviceId');
        // limit nur persistieren, wenn != Default — haelt Reset → /transactions sauber.
        if (next.limit && next.limit !== DEFAULT_TX_LIMIT) params.set('limit', String(next.limit));
        else params.delete('limit');
        // Server-Filter-Wechsel resettet die Blaetterung; stale `cursor` defensiv entfernen.
        params.delete('cursor');
      });
    },
    [update],
  );

  const setAmountMin = useCallback(
    (value: string) => update((params) => setOrDelete(params, 'amountMin', value || undefined)),
    [update],
  );

  const setAmountMax = useCallback(
    (value: string) => update((params) => setOrDelete(params, 'amountMax', value || undefined)),
    [update],
  );

  const setDateRange = useCallback(
    (range: DateRange) =>
      update((params) => {
        setOrDelete(params, 'dateFrom', range.from ? formatDateParam(range.from) : undefined);
        setOrDelete(params, 'dateTo', range.to ? formatDateParam(range.to) : undefined);
      }),
    [update],
  );

  // Spec 380 §4.3: Reset leert URL UND Snapshot — nach Reset seedet das naechste Mount
  // nichts Stale.
  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams());
    setSnapshot({});
  }, [setSearchParams, setSnapshot]);

  return {
    filters,
    amountMin,
    amountMax,
    dateFrom,
    dateTo,
    hasActiveFilters,
    setFilters,
    setAmountMin,
    setAmountMax,
    setDateRange,
    reset,
  };
}
